import type { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'

import FilterGrainImage from 'src/shared/assets/images/filter-grain.png'
import StepProgressBar from 'src/shared/components/ui/step-progress-bar'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'

import FamilyPlayingImage from '../assets/family-playing-image.jpg'

import EnterpriseSidePanelImage from './assets/enterprise-sidepanel-image.jpg'

interface SideContentItemProps {
  image: string
  imageAlt: string
  title: string
  description: string
}

function SideContentItem({ image, imageAlt, title, description }: SideContentItemProps) {
  return (
    <>
      <div className="h-[241px] w-full overflow-hidden rounded-[30px] xs:h-[420px]">
        <img src={image} alt={imageAlt} className="size-full object-cover" />
      </div>
      <div className="mt-[24px] flex flex-col justify-between px-[25px] xs:mt-10 xs:px-[30px]">
        <div>
          <h2 className="mb-2 text-[14px] font-semibold leading-tight tracking-[0.7px] text-secondary xs:text-[16px] xs:tracking-[0.8px]">
            FOR ENTERPRISE
          </h2>
          <p className="mb-4 text-[22px] font-semibold leading-tight tracking-[-0.11px] text-primary xs:text-[26px] xs:tracking-[-0.13px]">
            {title}
          </p>
          <p className="text-[15px] font-normal leading-[1.40] tracking-[-0.075px] text-muted xs:text-[18px] xs:tracking-[-0.09px]">
            {description}
          </p>
        </div>
      </div>
    </>
  )
}

const AUTO_SCROLL_INTERVAL = 8000 // in milliseconds
const AUTO_SCROLL_SPEED = 50 // use values between 20-60.
const CAROUSEL_DATA: SideContentItemProps[] = [
  {
    image: EnterpriseSidePanelImage,
    imageAlt: 'Enterprise Side Panel Image',
    title: 'The best real estate network now available for enterprises.',
    description: "Lynk screens, follows up, and nurtures leads so you don't waste hours chasing the wrong ones.",
  },
  {
    image: EnterpriseSidePanelImage,
    imageAlt: 'Enterprise Side Panel Image',
    title: 'The best real estate network now available for enterprises.',
    description: "Lynk screens, follows up, and nurtures leads so you don't waste hours chasing the wrong ones.",
  },
  {
    image: EnterpriseSidePanelImage,
    imageAlt: 'Enterprise Side Panel Image',
    title: 'The best real estate network now available for enterprises.',
    description: "Lynk screens, follows up, and nurtures leads so you don't waste hours chasing the wrong ones.",
  },
  {
    image: EnterpriseSidePanelImage,
    imageAlt: 'Enterprise Side Panel Image',
    title: 'The best real estate network now available for enterprises.',
    description: "Lynk screens, follows up, and nurtures leads so you don't waste hours chasing the wrong ones.",
  },
]

function SideContent() {
  const [step, setStep] = useState<number>(0)
  const { isMobile } = useBreakpoint()
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    skipSnaps: false,
    startIndex: 0,
    duration: AUTO_SCROLL_SPEED,
  })
  const totalSteps = CAROUSEL_DATA.length

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setStep(emblaApi.selectedScrollSnap())
  }, [])

  const onNext = useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) {
      return
    }
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  useEffect(() => {
    let interval: NodeJS.Timeout
    const startInterval = () => {
      return setInterval(() => {
        // Only auto-scroll if the page is not hidden(user tabbed out).
        if (!document.hidden) {
          onNext()
        }
      }, AUTO_SCROLL_INTERVAL)
    }
    const onPointerDown = () => {
      clearInterval(interval)
    }
    const onPointerUp = () => {
      interval = startInterval()
    }
    // Auto-scroll the carousel when the page is loaded.
    interval = startInterval()
    // Stop the auto-scroll when the user interacts with the carousel.
    emblaApi?.on('pointerDown', onPointerDown)
    // Resume the auto-scroll when the user stops interacting with the carousel.
    emblaApi?.on('pointerUp', onPointerUp)
    return () => {
      emblaApi?.off('pointerDown', onPointerDown)
      emblaApi?.off('pointerUp', onPointerUp)
      clearInterval(interval)
    }
  }, [onNext])

  return (
    <div className={cn('flex flex-1 flex-col justify-between px-5 pb-[25px] pt-5', isMobile && 'px-0 pb-5 pt-0')}>
      <div ref={emblaRef} className="flex-1 overflow-hidden rounded-[30px]">
        <div className="flex flex-row">
          {CAROUSEL_DATA.map((item, index) => (
            <div key={index} className="mr-5 w-full shrink-0 select-none">
              <SideContentItem
                image={item.image}
                imageAlt={item.imageAlt}
                title={item.title}
                description={item.description}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-[35px] flex w-full shrink-0 justify-center">
        <StepProgressBar
          totalSteps={totalSteps}
          currentStep={step + 1}
          className="w-[97px] gap-[4px]"
          stepClassName="h-[4px]"
          completedStepClassName="flex-[3] bg-[#D8D8D6]"
          incompleteStepClassName="flex-[3] bg-[#D8D8D6]"
          currentStepClassName="flex-[8] bg-primary"
        />
      </div>
    </div>
  )
}

const ConsumerSideContent = () => {
  return (
    <div className="flex flex-1 flex-col">
      <img src={FamilyPlayingImage} alt="Family Playing Image" className="flex-1 object-cover object-[35%_50%]" />
      <img src={FilterGrainImage} className="pointer-events-none absolute inset-0 z-10 size-full object-cover" />
    </div>
  )
}

interface EnterpriseAuthSideContentProps {
  variant?: 'default' | 'consumer'
}

export default function EnterpriseAuthSideContent({ variant = 'default' }: EnterpriseAuthSideContentProps) {
  const { isMobile } = useBreakpoint()

  return (
    <div
      className={cn(
        'relative flex flex-1 flex-col overflow-hidden rounded-[40px] bg-white',
        isMobile && 'w-full border border-border shadow-[0_2px_20px_0_rgba(27,48,41,0.05)]',
      )}
    >
      {variant === 'default' && <SideContent />}
      {variant === 'consumer' && <ConsumerSideContent />}
    </div>
  )
}
